import React, { createContext, useCallback, useContext, useState } from "react";
import { Btn } from "./primitives";
import { Warning, X } from "@phosphor-icons/react";

/**
 * Global confirmation dialog. Use `useConfirm()` then call
 *   const ok = await confirm({ title, description, variant: 'danger' });
 */
const ConfirmCtx = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({ ...opts, resolve });
    });
  }, []);

  const close = (result) => {
    if (state?.resolve) state.resolve(result);
    setState(null);
  };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-[100]"
          onClick={() => close(false)}
          data-testid="confirm-dialog-overlay"
        >
          <div
            className="swiss-card w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="confirm-dialog"
          >
            <div className="flex items-start gap-4 mb-4">
              <div className={`w-10 h-10 flex items-center justify-center rounded-sm ${
                state.variant === "danger" ? "bg-[#FF3B30]/10 text-[#FF3B30]" : "bg-[var(--brand-soft)] text-[var(--brand)]"
              }`}>
                <Warning size={20} weight="fill" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-lg font-semibold text-[var(--ink)]">{state.title || "Bestätigen?"}</h3>
                {state.description && <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">{state.description}</p>}
              </div>
              <button onClick={() => close(false)} className="text-[var(--muted)] hover:text-[var(--ink)]" data-testid="confirm-dialog-close">
                <X size={18} />
              </button>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => close(false)} data-testid="confirm-dialog-cancel">
                {state.cancelText || "Abbrechen"}
              </Btn>
              <Btn
                variant={state.variant === "danger" ? "danger" : "primary"}
                onClick={() => close(true)}
                data-testid="confirm-dialog-confirm"
              >
                {state.confirmText || "Bestätigen"}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}

export const useConfirm = () => {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used inside ConfirmProvider");
  return ctx;
};
